/**
 * @file getResumeSearchQuery
 *
 * @fileoverview Gets all fields for the resume search query. This function checks all optional flags that should add fields from the query
 * making sure that these are not sent if the flags are turned off.
 */
import { gql, useQuery } from '@apollo/client';
import { useContext } from 'react';
import { getResumeFragment } from '~/jobiqo/components/Resume/graphql/fragments/resumeFragment';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { AppContextInterface, PathContextInterface } from '~/jobiqo/models/app';
import { FacetConfig } from '~/jobiqo/models/search';
import { getQueryVariables } from '../../Search/utils/searchHelpers';
import { getResumeSortingFilterOptions } from '~/jobiqo/components/ResumeSearch/components/SortingFilter/utils';
import CUSTOM_RESUME_FIELDS from '~/graphql/queries/resume/customResumeFields.gql';
import { useConfig } from '~/jobiqo/context/config';
import { ResumeFrontendFeatureFlags } from '~/jobiqo/components/Resume/graphql/resumeFeatureFlags';

const resumeSortingOptions = getResumeSortingFilterOptions();

export const getResumeSearchQuery = (
  currentOrganizationId: null | number = null,
  bookmarkEnabled: boolean,
  contactRequestEnabled: boolean | null = null,
  frontendFlags: ResumeFrontendFeatureFlags = {},
) => {
  const RESUME_FRAGMENT = getResumeFragment(
    currentOrganizationId,
    bookmarkEnabled,
    frontendFlags,
  );

  return gql`
      ${RESUME_FRAGMENT}
      ${CUSTOM_RESUME_FIELDS}
      query resumeSearch(
        $range: RangeInput!
        $sorts: [SortInput]
        $language: String!
        $condition_group: ConditionGroupInput!
        $fulltext: FulltextInput!
        $facets: [FacetInput]!
        $geoLocation: GeoInput
      ) {
        resumes(
          ranges: $range
          sorts: $sorts
          language: [$language]
          condition_group: $condition_group
          fulltext: $fulltext
          facets: $facets
          geo_input: $geoLocation
        ) {
          result_count
          metadata: metatags {
            meta
            value
          }
          pages {
            ... on Resume {
              ...ResumeFragment
              ...CustomResumeFields
              ${
                currentOrganizationId && contactRequestEnabled
                  ? `
                    resumeContact(organizationId: ${currentOrganizationId}){
                      id
                      recruiterId
                      state
                      changed
                    }
                  `
                  : ``
              }
            }
          }
          facets {
            name
            values {
              label
              filter
              totalCount
              alias
              children {
                label
                filter
                totalCount
                alias
              }
            }
          }
        }
      }
    `;
};

/**
 * Helper hook to run query for getting resumes on resume search.
 *
 * @returns
 *   The error and data from the useQuery hook.
 */
export const useResumeSearchQuery = () => {
  const { user }: UserContextInterface = useContext(UserContext);
  const appContext: AppContextInterface = useContext(AppContext);
  const pathContext: PathContextInterface = useContext(PathContext);
  const { RESUME, JOBS } = useFrontendConfig();
  const resumeSearchListingNumber = RESUME.RESUME_SEARCH_LISTING_NUMBER;
  const resumeSearchFacetsConfig: FacetConfig[] = RESUME.FACETS;
  const { getConfigByName } = useConfig();
  const RESUME_BOOKMARK_ENABLED: boolean = !!getConfigByName(
    'jobiqo_resume_bookmark',
  )?.value[0];
  const RESUME_CONTACT_FEATURE_ACTIVE: boolean = !!getConfigByName(
    'resume_contact_enabled',
  )?.value[0];

  const queryVars = getQueryVariables(
    pathContext.querystring,
    resumeSearchListingNumber,
    resumeSearchFacetsConfig,
    appContext.locale,
    resumeSortingOptions,
  );

  // Dynamic default sorting for resume search:
  // If no sorting param in URL, sort by "Last update" (changed desc) when
  // there's no search term or location; otherwise use relevance by default.
  if (!pathContext.querystring.sorting && !queryVars.sorts) {
    const hasSearchOrGeo =
      !!pathContext.querystring.search ||
      !!pathContext.querystring.geo_location;
    if (!hasSearchOrGeo) {
      queryVars.sorts = [{ field: 'changed', value: 'desc' }];
    }
  }

  const { error, previousData, data = previousData } = useQuery(
    getResumeSearchQuery(
      user?.activeOrganization?.id || null,
      RESUME_BOOKMARK_ENABLED,
      RESUME_CONTACT_FEATURE_ACTIVE,
      {
        RESUME_BADGES_ACTIVE: RESUME.RESUME_BADGES_ACTIVE,
        FIELDS_OF_STUDY_FEATURE_ACTIVE: RESUME.FIELDS_OF_STUDY_FEATURE_ACTIVE,
        SEASONAL_JOBS_ENABLED: JOBS.SEASONAL_JOBS_ENABLED,
      },
    ),
    {
      variables: queryVars,
    },
  );

  return { error, data };
};

/**
 * Helper hook to get the query to run to refetch resume search.
 *
 * @returns
 *   Returns the query to refetch resumes on resume search.
 */
export const useRefetchResumeSearchQuery = () => {
  const { user }: UserContextInterface = useContext(UserContext);
  const appContext: AppContextInterface = useContext(AppContext);
  const pathContext: PathContextInterface = useContext(PathContext);
  const { RESUME, JOBS } = useFrontendConfig();
  const resumeSearchListingNumber = RESUME.RESUME_SEARCH_LISTING_NUMBER;
  const resumeSearchFacetsConfig: FacetConfig[] = RESUME.FACETS;
  const { getConfigByName } = useConfig();
  const RESUME_BOOKMARK_ENABLED: boolean = !!getConfigByName(
    'jobiqo_resume_bookmark',
  )?.value[0];
  const RESUME_CONTACT_FEATURE_ACTIVE: boolean = !!getConfigByName(
    'resume_contact_enabled',
  )?.value[0];

  const refetchQueryVars = getQueryVariables(
    pathContext.querystring,
    resumeSearchListingNumber,
    resumeSearchFacetsConfig,
    appContext.locale,
    resumeSortingOptions,
  );

  // Dynamic default sorting for resume search refetch.
  if (!pathContext.querystring.sorting && !refetchQueryVars.sorts) {
    const hasSearchOrGeo =
      !!pathContext.querystring.search ||
      !!pathContext.querystring.geo_location;
    if (!hasSearchOrGeo) {
      refetchQueryVars.sorts = [{ field: 'changed', value: 'desc' }];
    }
  }

  const refetchResumeSearchQuery = {
    query: getResumeSearchQuery(
      user?.activeOrganization?.id || null,
      RESUME_BOOKMARK_ENABLED,
      RESUME_CONTACT_FEATURE_ACTIVE,
      {
        RESUME_BADGES_ACTIVE: RESUME.RESUME_BADGES_ACTIVE,
        FIELDS_OF_STUDY_FEATURE_ACTIVE: RESUME.FIELDS_OF_STUDY_FEATURE_ACTIVE,
        SEASONAL_JOBS_ENABLED: JOBS.SEASONAL_JOBS_ENABLED,
      },
    ),
    variables: {
      ...refetchQueryVars,
      resumeContactActive: RESUME_CONTACT_FEATURE_ACTIVE,
    },
  };

  return { refetchResumeSearchQuery };
};
